/**
 * Redux slice for Textile Costing consolidated view.
 *
 * Manages state for:
 * - Consolidated costing data (all products for an opportunity)
 * - UI state (expanded sections, editing mode)
 * - Edit tracking and undo/redo
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { textileCostingService } from '../../services/textileCostingService';

// Initial state
const initialState = {
  // Data
  opportunity: null,
  products: [],
  summary: null,

  // Original data (for reset functionality)
  originalData: null,

  // Edit tracking
  pendingEdits: {},
  hasUnsavedChanges: false,

  // UI state
  expandedProducts: {}, // { productId: true/false }
  expandedSections: {}, // { productId: { greige: true, finished: true, madeup: true } }
  editingField: null,

  // Loading states
  isLoading: false,
  isCalculating: false,
  isSaving: false,
  error: null,

  // List view
  opportunitiesList: [],
  isLoadingList: false,

  // Opportunity-level approval
  approvalStatus: null,
  workflowSteps: [],
  canSubmit: false,
  canCancel: false,
  validationErrors: [],
  isSubmittingApproval: false,
};

// Async thunks
export const fetchConsolidatedCosting = createAsyncThunk(
  'textileCosting/fetchConsolidated',
  async (opportunityId, { rejectWithValue }) => {
    try {
      const response = await textileCostingService.getConsolidatedCosting(opportunityId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: 'Failed to fetch costing data' }
      );
    }
  }
);

export const fetchOpportunitiesWithCosting = createAsyncThunk(
  'textileCosting/fetchOpportunities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await textileCostingService.getOpportunitiesWithCosting();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: 'Failed to fetch opportunities' }
      );
    }
  }
);

export const updateCostingParameter = createAsyncThunk(
  'textileCosting/updateParameter',
  async ({ costingType, costingId, data }, { rejectWithValue }) => {
    try {
      const response = await textileCostingService.updateCosting(
        costingType,
        costingId,
        data
      );
      return { costingType, costingId, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: 'Failed to update costing' }
      );
    }
  }
);

export const recalculateCosting = createAsyncThunk(
  'textileCosting/recalculate',
  async ({ costingType, costingId }, { rejectWithValue }) => {
    try {
      const response = await textileCostingService.recalculateCosting(
        costingType,
        costingId
      );
      return { costingType, costingId, result: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: 'Failed to recalculate' }
      );
    }
  }
);

export const performApprovalAction = createAsyncThunk(
  'textileCosting/approvalAction',
  async ({ costingType, costingId, action, comments }, { rejectWithValue }) => {
    try {
      const response = await textileCostingService.approvalAction(
        costingType,
        costingId,
        action,
        comments
      );
      return { costingType, costingId, result: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: 'Failed to perform action' }
      );
    }
  }
);

// Opportunity-level approval thunks
export const submitOpportunityForApproval = createAsyncThunk(
  'textileCosting/submitOpportunityForApproval',
  async (opportunityId, { rejectWithValue }) => {
    try {
      const response = await textileCostingService.submitOpportunityForApproval(opportunityId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: 'Failed to submit for approval' }
      );
    }
  }
);

export const cancelOpportunityApproval = createAsyncThunk(
  'textileCosting/cancelOpportunityApproval',
  async ({ opportunityId, reason }, { rejectWithValue }) => {
    try {
      const response = await textileCostingService.cancelOpportunityApproval(opportunityId, reason);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: 'Failed to cancel approval' }
      );
    }
  }
);

export const fetchApprovalStatus = createAsyncThunk(
  'textileCosting/fetchApprovalStatus',
  async (opportunityId, { rejectWithValue }) => {
    try {
      const response = await textileCostingService.getOpportunityApprovalStatus(opportunityId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { error: 'Failed to fetch approval status' }
      );
    }
  }
);

// Slice
const textileCostingSlice = createSlice({
  name: 'textileCosting',
  initialState,
  reducers: {
    // Clear state
    clearCostingData: (state) => {
      state.opportunity = null;
      state.products = [];
      state.summary = null;
      state.originalData = null;
      state.pendingEdits = {};
      state.hasUnsavedChanges = false;
      state.error = null;
    },

    clearError: (state) => {
      state.error = null;
    },

    // UI state management
    toggleProductExpanded: (state, action) => {
      const productId = action.payload;
      state.expandedProducts[productId] = !state.expandedProducts[productId];
    },

    setProductExpanded: (state, action) => {
      const { productId, expanded } = action.payload;
      state.expandedProducts[productId] = expanded;
    },

    toggleSectionExpanded: (state, action) => {
      const { productId, section } = action.payload;
      if (!state.expandedSections[productId]) {
        state.expandedSections[productId] = { greige: false, finished: false, madeup: true };
      }
      state.expandedSections[productId][section] =
        !state.expandedSections[productId][section];
    },

    setSectionExpanded: (state, action) => {
      const { productId, section, expanded } = action.payload;
      if (!state.expandedSections[productId]) {
        state.expandedSections[productId] = { greige: false, finished: false, madeup: true };
      }
      state.expandedSections[productId][section] = expanded;
    },

    expandAllProducts: (state) => {
      state.products.forEach((product, index) => {
        const pid = product.product_id || product.madeup?.id || index;
        if (pid) {
          state.expandedProducts[pid] = true;
        }
      });
    },

    collapseAllProducts: (state) => {
      state.expandedProducts = {};
    },

    // Editing
    setEditingField: (state, action) => {
      state.editingField = action.payload;
    },

    setPendingEdit: (state, action) => {
      const { costingType, costingId, field, value } = action.payload;
      const key = `${costingType}_${costingId}`;
      if (!state.pendingEdits[key]) {
        state.pendingEdits[key] = {};
      }
      state.pendingEdits[key][field] = value;
      state.hasUnsavedChanges = true;
    },

    clearPendingEdits: (state) => {
      state.pendingEdits = {};
      state.hasUnsavedChanges = false;
    },

    // Reset to original
    resetToOriginal: (state) => {
      if (state.originalData) {
        state.opportunity = state.originalData.opportunity;
        state.products = state.originalData.products;
        state.summary = state.originalData.summary;
        state.pendingEdits = {};
        state.hasUnsavedChanges = false;
      }
    },

    // Update local product data after edit
    updateProductData: (state, action) => {
      const { productIndex, data } = action.payload;
      if (state.products[productIndex]) {
        state.products[productIndex] = {
          ...state.products[productIndex],
          ...data,
        };
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Fetch consolidated costing
      .addCase(fetchConsolidatedCosting.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchConsolidatedCosting.fulfilled, (state, action) => {
        state.isLoading = false;
        state.opportunity = action.payload.opportunity;
        state.products = action.payload.products;
        state.summary = action.payload.summary;
        // Store original for reset
        state.originalData = {
          opportunity: action.payload.opportunity,
          products: action.payload.products,
          summary: action.payload.summary,
        };
        // Initialize expanded state - first product expanded by default
        // Use product_id (stable key from backend) with fallback to madeup.id
        if (action.payload.products.length > 0) {
          const firstProduct = action.payload.products[0];
          const pid = firstProduct.product_id || firstProduct.madeup?.id;
          if (pid) {
            state.expandedProducts[pid] = true;
            state.expandedSections[pid] = {
              greige: false,
              finished: false,
              madeup: true,
            };
          }
        }
        state.error = null;
      })
      .addCase(fetchConsolidatedCosting.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.error || 'Failed to fetch costing data';
      })

      // Fetch opportunities list
      .addCase(fetchOpportunitiesWithCosting.pending, (state) => {
        state.isLoadingList = true;
      })
      .addCase(fetchOpportunitiesWithCosting.fulfilled, (state, action) => {
        state.isLoadingList = false;
        state.opportunitiesList = action.payload;
      })
      .addCase(fetchOpportunitiesWithCosting.rejected, (state, action) => {
        state.isLoadingList = false;
        state.error = action.payload?.error || 'Failed to fetch opportunities';
      })

      // Update costing parameter
      .addCase(updateCostingParameter.pending, (state) => {
        state.isCalculating = true;
      })
      .addCase(updateCostingParameter.fulfilled, (state, action) => {
        state.isCalculating = false;
        // Update the relevant costing in products array
        const { costingType, costingId, data } = action.payload;
        state.products = state.products.map((product) => {
          if (costingType === 'madeup' && product.madeup?.id === costingId) {
            return { ...product, madeup: data };
          }
          // Update finished fabric in the product's finished_fabrics array
          if (costingType === 'finished') {
            return {
              ...product,
              finished_fabrics: product.finished_fabrics.map((ff) =>
                ff.id === costingId ? data : ff
              ),
            };
          }
          // Update greige fabric in the product's greige_fabrics array
          if (costingType === 'greige') {
            return {
              ...product,
              greige_fabrics: product.greige_fabrics.map((gf) =>
                gf.id === costingId ? data : gf
              ),
            };
          }
          return product;
        });
        // Clear pending edit for this costing
        const key = `${costingType}_${costingId}`;
        delete state.pendingEdits[key];
        state.hasUnsavedChanges = Object.keys(state.pendingEdits).length > 0;
      })
      .addCase(updateCostingParameter.rejected, (state, action) => {
        state.isCalculating = false;
        state.error = action.payload?.error || 'Failed to update costing';
      })

      // Recalculate
      .addCase(recalculateCosting.pending, (state) => {
        state.isCalculating = true;
      })
      .addCase(recalculateCosting.fulfilled, (state) => {
        state.isCalculating = false;
      })
      .addCase(recalculateCosting.rejected, (state, action) => {
        state.isCalculating = false;
        state.error = action.payload?.error || 'Failed to recalculate';
      })

      // Approval action (per-sheet, kept for backward compat)
      .addCase(performApprovalAction.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(performApprovalAction.fulfilled, (state, action) => {
        state.isSaving = false;
        const { costingType, costingId, result } = action.payload;
        state.products = state.products.map((product) => {
          if (costingType === 'madeup' && product.madeup?.id === costingId) {
            return {
              ...product,
              madeup: {
                ...product.madeup,
                status: result.status,
                status_display: result.status_display,
              },
            };
          }
          return product;
        });
      })
      .addCase(performApprovalAction.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload?.error || 'Failed to perform action';
      })

      // Submit opportunity for approval
      .addCase(submitOpportunityForApproval.pending, (state) => {
        state.isSubmittingApproval = true;
      })
      .addCase(submitOpportunityForApproval.fulfilled, (state, action) => {
        state.isSubmittingApproval = false;
        state.approvalStatus = action.payload.approval_status;
        if (state.opportunity) {
          state.opportunity.approval_status = action.payload.approval_status;
          state.opportunity.approval_status_display = action.payload.approval_status_display;
        }
      })
      .addCase(submitOpportunityForApproval.rejected, (state, action) => {
        state.isSubmittingApproval = false;
        state.error = action.payload?.error || 'Failed to submit for approval';
        state.validationErrors = action.payload?.validation_errors || [];
      })

      // Cancel opportunity approval
      .addCase(cancelOpportunityApproval.pending, (state) => {
        state.isSubmittingApproval = true;
      })
      .addCase(cancelOpportunityApproval.fulfilled, (state, action) => {
        state.isSubmittingApproval = false;
        state.approvalStatus = action.payload.approval_status;
        if (state.opportunity) {
          state.opportunity.approval_status = action.payload.approval_status;
          state.opportunity.approval_status_display = action.payload.approval_status_display;
        }
      })
      .addCase(cancelOpportunityApproval.rejected, (state, action) => {
        state.isSubmittingApproval = false;
        state.error = action.payload?.error || 'Failed to cancel approval';
      })

      // Fetch approval status
      .addCase(fetchApprovalStatus.fulfilled, (state, action) => {
        state.approvalStatus = action.payload.approval_status;
        state.workflowSteps = action.payload.steps || [];
        state.canSubmit = action.payload.can_submit;
        state.canCancel = action.payload.can_cancel;
        state.validationErrors = action.payload.validation_errors || [];
      });
  },
});

// Export actions
export const {
  clearCostingData,
  clearError,
  toggleProductExpanded,
  setProductExpanded,
  toggleSectionExpanded,
  setSectionExpanded,
  expandAllProducts,
  collapseAllProducts,
  setEditingField,
  setPendingEdit,
  clearPendingEdits,
  resetToOriginal,
  updateProductData,
} = textileCostingSlice.actions;

// Selectors
export const selectOpportunity = (state) => state.textileCosting.opportunity;
export const selectProducts = (state) => state.textileCosting.products;
export const selectSummary = (state) => state.textileCosting.summary;
export const selectIsLoading = (state) => state.textileCosting.isLoading;
export const selectIsLoadingList = (state) => state.textileCosting.isLoadingList;
export const selectIsCalculating = (state) => state.textileCosting.isCalculating;
export const selectError = (state) => state.textileCosting.error;
export const selectHasUnsavedChanges = (state) => state.textileCosting.hasUnsavedChanges;
export const selectExpandedProducts = (state) => state.textileCosting.expandedProducts;
export const selectExpandedSections = (state) => state.textileCosting.expandedSections;
export const selectOpportunitiesList = (state) => state.textileCosting.opportunitiesList;
export const selectApprovalStatus = (state) => state.textileCosting.approvalStatus;
export const selectWorkflowSteps = (state) => state.textileCosting.workflowSteps;
export const selectCanSubmit = (state) => state.textileCosting.canSubmit;
export const selectCanCancel = (state) => state.textileCosting.canCancel;
export const selectValidationErrors = (state) => state.textileCosting.validationErrors;
export const selectIsSubmittingApproval = (state) => state.textileCosting.isSubmittingApproval;

export default textileCostingSlice.reducer;
